import { Component, OnInit } from '@angular/core';
import { Observable, of } from 'rxjs';
import {
  catchError,
  finalize,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs/operators';

import {
  ComparisonObjectsWithLogicalOperators,
  CustomResultOfListOfGetPlansTypeModuleDto,
  PlanDetails,
  PlanListItemForBuyDto,
} from 'src/app/core/api/models';
import { PlansService } from 'src/app/core/api/services';
import { AppDomService } from 'src/app/core/services/dom.service';
import { DynamicTabService } from 'src/app/core/services/dynamic-tab.service';
import { RefreshMenuService } from 'src/app/core/services/refresh-menu.service';
import { TabsDynamicComponent } from 'src/app/layout/components/tabs-dynamic/tabs-dynamic.component';
import { EventTypes } from '../../../../core/constants/event-types';
import { BaseIndexPageComponent } from '../../../shared/base/components/base-index-page.component';
import { PlansTypeModuleForUserFormComponent } from '../plans-type-module-for-user-form/plans-type-module-for-user-form.component';

export interface ListOfPlanListItemForBuyDto {
  items?: Array<PlanListItemForBuyDto>;
  totalCount?: number;
}

@Component({
    selector: 'app-get-plans-type-module',
    templateUrl: './get-plans-type-module.component.html',
    styleUrls: ['./get-plans-type-module.component.scss'],
    standalone: false
})
export class GetPlansTypeModuleComponent
  extends BaseIndexPageComponent
  implements OnInit
{
  //#region properties

  pageName = 'plans.planListModuleForUser';
  modelWithPagination$: Observable<PlanListItemForBuyDto[]>;
  items$: Observable<Array<PlanListItemForBuyDto>>;
  items: PlanListItemForBuyDto[] = [];

  buyButtonLoading: boolean = false;
  planTypeModule: PlanListItemForBuyDto;
  vocabsPatch: string;
  discountAmount: number = 0;
  discountLoading: boolean = false;
  discountCode: string;
  userCredit: number;
  finalAmount: number;
  isConfirm: boolean = false;
  tabName: string = 'getPlansTypeModule';
  // isSuccess: boolean = false;
  isSuccessPurchase: boolean = false;
  verifyDescription: string = '';
  translatedType: string;

  //milliseconds delay after the user stops scrolling;
  throttle = 300;
  // this event is triggered when 90% (100% - 10%) has been scrolled
  infiniteScrollDistance = 1; // 1 ==> 10%

  //#endregion

  constructor(
    protected currentService: PlansService,
    protected dynamicTabService: DynamicTabService,
    public refreshMenuService: RefreshMenuService,
    protected tabsDynamicComponent: TabsDynamicComponent
  ) {
    super();
  }

  ngOnInit() {
    try {
      super.ngOnInit();
      this.pageConfig.showAddBtn = false;
      this.pageConfig.showAuditBtn = false;
      this.pageConfig.isShowTable = false;
    } catch (error) {
      this.loggerService.logError(this.tagName, error, EventTypes.plans);
    }
  }

  initTablePagination() {
    this.paginationModel.pageIndex = 1;
    this.paginationModel.firstIndex = 1;
    this.paginationModel.pageNumber = 1;
    this.paginationModel.pageSize = 12;
  }

  bindTableData() {
    if (this.isBindTableData) return;
    this.isBindTableData = true;
    this.tableDataSubject$
      .pipe(
        switchMap((res) => {
          this.pageConfig.loading = true;
          return this.getPaginationApi().pipe(
            tap((result) => {
              this.modelWithPagination$ = of(result?.data);
            }),
            finalize(() => (this.pageConfig.loading = false)),
            catchError((error) => {
              this.pageConfig.isEmpty = false;
              this.pageConfig.hasError = true;
              return error;
            }),
            takeUntil(this.onDestroy$),
            finalize(() => {
              this.pageConfig.loading = false;
            })
          );
        })
      )
      .subscribe((res: CustomResultOfListOfGetPlansTypeModuleDto) => {
        this.checkSelectedItems();
        this.getPaginationData(res?.data);
        this.items = res?.data as PlanListItemForBuyDto[];
        this.items$ = of(this.items);
        this.items?.length > 0
          ? (this.pageConfig.isEmpty = false)
          : (this.pageConfig.isEmpty = true);
        this.pageConfig.loading = false;
      });
  }

  configTableColumns(): void {}

  //#region api

  //get All
  getPaginationApi(): Observable<CustomResultOfListOfGetPlansTypeModuleDto> {
    return this.currentService.plansGetPlansTypeModule({
      body: this.getBaseModelWithPaginationParams(),
    });
  }

  //Create
  baseCreateGetApi(selectedPlanModule: PlanListItemForBuyDto) {
    this.unSubs();

    this.buyButtonLoading = true;
    this.currentService
      .plansGetPlan({ body: { id: selectedPlanModule.id } })
      .pipe(
        finalize(() => {
          this.buyButtonLoading = false;
        }),
        takeUntil(this.onDestroy$)
      )
      .subscribe((res) => {
        if (res) this.onBuyPlanTypeModule(res?.data);
      });
  }

  //#endregion

  onBuyPlanTypeModule(planTypeModule: PlanDetails) {
    this.unSubs();

    const modalResult = this.dialogService.openModal({
      component: PlansTypeModuleForUserFormComponent,
      data: {
        planTypeModule: planTypeModule,
        vocabsPatch: this.formPageConfig.vocabs,
      },
    });

    modalResult
      .afterClosed()
      .pipe(
        takeUntil(this.onDestroy$),
        tap(() => {
          AppDomService.removeModalScrollClassFromHtml();
        })
      )
      .subscribe((res) => {
        if (res) this.openPlanTypeModuleBuyRequests();
      });
  }

  openPlanTypeModuleBuyRequests() {
    this.tabsDynamicComponent.manualCloseTab(this.tabName);
    this.dynamicTabService.setActiveTab('dashboard');
  }

  onScrollDown() {
    try {
      const totalCount = this.paginationModel.totalCount;

      if (this.items.length == totalCount) return;

      const totalPages = this.paginationModel.totalPages;
      const pageNumber = this.paginationModel.pageNumber;

      if (this.pageConfig.loading) return;

      this.pageConfig.loading = true;

      if (pageNumber < totalPages) this.paginationModel.pageNumber++;

      this.getTableData();
    } catch (error) {
      this.loggerService.logError(this.tagName, error, EventTypes.plans);
    }
  }

  trackByFunc(index: number, item: PlanListItemForBuyDto) {
    try {
      return item.id;
    } catch (error) {
      this.loggerService.logError(this.tagName, error, EventTypes.plans);
    }
  }

  getComparisonObjects(model: ComparisonObjectsWithLogicalOperators[]) {
    this.items = [];
    this.paginationModel.pageNumber = 1;
    this.comparisonObjects = model;
    this.pageConfig.tableConfig.comparisonObjects = Object.assign(
      [],
      this.comparisonObjects
    );
    this.getTableData();
  }
}
